"use client";

import { useEffect, useMemo, useState } from "react";
import { FlashcardDeck } from "@/components/lesson/flashcard";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { ErrorState, LoadingState } from "@/components/ui/state";
import { apiFetch, getPaginatedData } from "@/lib/api";
import type { Course, Lesson, Paginated, Vocabulary } from "@/types";

export function VocabularyTrainer() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [words, setWords] = useState<Vocabulary[]>([]);
  const [courseId, setCourseId] = useState("");
  const [lessonId, setLessonId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch<Paginated<Course> | Course[]>("/courses?limit=100")
      .then((payload) => {
        const data = getPaginatedData(payload);
        setCourses(data);
        setCourseId(data[0]?.id ?? "");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Không thể tải khóa học"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!courseId) {
      return;
    }
    setLoading(true);
    apiFetch<Lesson[]>(`/courses/${courseId}/lessons`)
      .then((data) => {
        setLessons(data);
        setLessonId(data[0]?.id ?? "");
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Không thể tải bài học"))
      .finally(() => setLoading(false));
  }, [courseId]);

  useEffect(() => {
    if (!lessonId) {
      setWords([]);
      return;
    }
    setLoading(true);
    apiFetch<Vocabulary[]>(`/lessons/${lessonId}/vocabulary`)
      .then(setWords)
      .catch((err) => setError(err instanceof Error ? err.message : "Không thể tải từ vựng"))
      .finally(() => setLoading(false));
  }, [lessonId]);

  const currentLesson = useMemo(() => lessons.find((lesson) => lesson.id === lessonId), [lessonId, lessons]);

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <Card className="h-fit">
        <h2 className="text-xl font-black text-ink">Bộ thẻ luyện tập</h2>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-2 block text-sm font-bold text-ink">Khóa học</span>
            <Select value={courseId} onChange={(event) => setCourseId(event.target.value)}>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.title}</option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-bold text-ink">Bài học</span>
            <Select value={lessonId} onChange={(event) => setLessonId(event.target.value)}>
              {lessons.map((lesson) => (
                <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
              ))}
            </Select>
          </label>
        </div>
        {currentLesson ? <p className="mt-5 text-sm leading-6 text-muted">{currentLesson.description}</p> : null}
      </Card>

      <div>
        {loading ? <LoadingState /> : words.length ? <FlashcardDeck words={words} /> : <Card>Chưa có từ vựng cho bài này.</Card>}
      </div>
    </div>
  );
}
